import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldAlert, KeyRound, ShieldCheck, Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, api } from '@/lib/api';
import { useAuth, isAdmin } from '@/lib/auth';

/**
 * Admin sign-in.
 *   1. password (email + password) → /auth/login.
 *   2a. If the admin has no confirmed authenticator yet → enroll: show the
 *       QR from /auth/mfa/enroll, confirm a code (/auth/mfa/confirm), then
 *       display the one-time recovery codes.
 *   2b. If already enrolled → verify a 6-digit TOTP (/auth/mfa/verify).
 *
 * Non-admin roles are signed back out immediately — the admin portal is
 * admin/superadmin only.
 */
type Step = 'password' | 'enroll' | 'recovery' | 'mfa';

interface EnrollData {
  secret: string;
  otpauth_url: string;
  qr_svg: string;
}

export function SignInPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const hydrate = useAuth((s) => s.hydrate);
  const signOut = useAuth((s) => s.signOut);
  const setMfaVerified = useAuth((s) => s.setMfaVerified);

  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enroll, setEnroll] = useState<EnrollData | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function beginEnroll() {
    // Password succeeded but no confirmed authenticator yet — fetch a fresh
    // secret + QR so the admin can set one up inline.
    const { data } = await api<{ data: EnrollData }>('/auth/mfa/enroll', { method: 'POST' });
    setEnroll(data);
    setCode('');
    setStep('enroll');
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api('/auth/login', { json: { email, password } });
      await hydrate();
      const user = useAuth.getState().user;
      if (!isAdmin(user)) {
        await signOut();
        setError('This account does not have admin access.');
        return;
      }
      if (!user.requires_mfa) {
        await signOut();
        setError('Multi-factor authentication is mandatory for admin access.');
        return;
      }
      if (user.mfa_enrolled) {
        setStep('mfa');
      } else {
        await beginEnroll();
      }
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "You're moving fast — please wait a moment and try again."
          : 'Sign-in failed. Check your email and password.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function submitEnrollConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api<{ data: { recovery_codes: string[] } }>('/auth/mfa/confirm', {
        json: { code },
      });
      setRecoveryCodes(data.recovery_codes);
      setMfaVerified(true);
      setCode('');
      setStep('recovery');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 422
          ? 'That code did not match. Make sure the authenticator is added, then use the current code.'
          : 'Could not confirm the authenticator. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function finishAfterRecovery() {
    await hydrate();
    navigate(next, { replace: true });
  }

  async function submitMfa(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api('/auth/mfa/verify', { json: { code } });
      setMfaVerified(true);
      await hydrate();
      navigate(next, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 422
          ? 'Code rejected. Try again.'
          : 'Verification failed.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  const description: Record<Step, string> = {
    password: 'Sign in with your admin credentials.',
    enroll: 'Set up an authenticator app to secure this admin account.',
    recovery: 'Save your recovery codes before continuing.',
    mfa: 'Enter the 6-digit code from your authenticator app.',
  };

  return (
    <section className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-10">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" aria-hidden />
            <CardTitle>GradeMyCV Admin</CardTitle>
          </div>
          <CardDescription>{description[step]}</CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'password' && (
            <form className="space-y-4" onSubmit={submitPassword}>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error !== null && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          )}

          {step === 'enroll' && enroll !== null && (
            <form className="space-y-4" onSubmit={submitEnrollConfirm}>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Open your authenticator app (Google Authenticator, Authy, 1Password…).</li>
                <li>Scan the QR code, or add the key manually.</li>
                <li>Enter the 6-digit code it shows to finish.</li>
              </ol>
              <div className="flex justify-center rounded-2xl border bg-white p-4">
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(enroll.qr_svg)}`}
                  alt="Authenticator setup QR code"
                  className="h-44 w-44"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Manual setup key</Label>
                <code className="block break-all rounded-lg bg-muted px-3 py-2 font-mono text-xs">
                  {enroll.secret}
                </code>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="enroll-code">Code from authenticator</Label>
                <Input
                  id="enroll-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              {error !== null && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={submitting || code.length !== 6}>
                <ShieldCheck className="mr-2 h-4 w-4" />
                {submitting ? 'Confirming…' : 'Confirm & enable'}
              </Button>
            </form>
          )}

          {step === 'recovery' && recoveryCodes !== null && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Store these one-time recovery codes somewhere safe. Each works once if you lose your
                authenticator. They won&apos;t be shown again.
              </p>
              <ul className="grid grid-cols-2 gap-2 rounded-2xl border bg-muted/40 p-3 font-mono text-xs">
                {recoveryCodes.map((c) => (
                  <li key={c} className="tracking-tight">
                    {c}
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => void navigator.clipboard?.writeText(recoveryCodes.join('\n'))}
              >
                <Copy className="mr-2 h-4 w-4" /> Copy codes
              </Button>
              <Button type="button" className="w-full" onClick={() => void finishAfterRecovery()}>
                I&apos;ve saved them — continue
              </Button>
            </div>
          )}

          {step === 'mfa' && (
            <form className="space-y-4" onSubmit={submitMfa}>
              <div className="space-y-1.5">
                <Label htmlFor="code">Authenticator code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              {error !== null && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={submitting || code.length !== 6}>
                <KeyRound className="mr-2 h-4 w-4" />
                {submitting ? 'Verifying…' : 'Verify code'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
